import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { ReleaseIdCardCrumbs } from 'front-office/constants/release-id-card-crumbs';
import { Fragment, useCallback, useEffect, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { validationSchema } from 'front-office/constants/release-id-card-schema';
import ReleaseIdCardForm from 'front-office/components/release-id-card/form';
import { ReleaseIdCard } from 'front-office/view-models/release-id-card';
import ReleaseIdCardButtons from 'front-office/components/release-id-card/buttons';
import { initialValues } from 'front-office/constants/release-id-card-initial-values';
import CancelModal from 'front-office/components/release-id-card/cancel-modal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { releaseIdCardResponseTransformer } from 'front-office/utils/release-id-card-response-transformer';
import {
  transformEnvelopePrintRequest,
  transformExternalPrintRequest,
  transformIdCardRequest,
  transformInternalPrintRequest,
  transformPrintTagRequestQuestura,
  transformPrintTagRequestComune,
} from 'front-office/utils/release-id-card-request-transformer';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ErrorModal from 'front-office/components/release-id-card/error-modal';
import { AxiosResponse } from 'axios';
// import { openPdf } from 'shell/utils/open-pdf';
import { toast } from 'react-toastify';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalWarning from 'shell/components/shared/modal-warning';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { LastIdCard, ReleasePaperIdCard, ResponseLot } from 'shell/models/last-id-card';
import { AnomalyType } from 'front-office/constants/anomaly';
import ModalPrintCid, {
  ButtonType,
  ModalType,
} from 'front-office/components/release-id-card/modal-print';
import { Citizenship } from 'shell/models/person/citizenship';
import { DateTime } from 'luxon';
import PmiTitle from 'shell/components/shared/title';

const ReleaseIdCardPage = (): React.ReactElement => {
  const [modalType, setModalType] = useState<ModalType>(ModalType.Cid);

  const [openCancelModal, setOpenCancelModal] = useState(false);

  const [openPrintModal, setOpenPrintModal] = useState(false);

  const [openIdCardErrorModal, setOpenIdCardErrorModal] = useState(false);

  const [openCardsErrorModal, setOpenCardsErrorModal] = useState(false);

  const [externalPrintVisibility, setExternalPrintVisibility] = useState(false);

  const [internalPrintVisibility, setInternalPrintVisibility] = useState(false);

  const [warningModal, setWarningModal] = useState({ open: false, value: '' });

  const [permitError, setPermitError] = useState({ open: false, value: '' });

  const [pressedButtons, setPressedButtons] = useState<Array<ButtonType>>([]);

  const [queryParams] = useSearchParams();

  const { userProfile } = useUserProfile();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');

  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const releaseIdCard = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carte-identita/rilascio-cid-cartacea`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const checkReleaseIdCard = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carte-identita/rilascio-cid-cartacea/check`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const [, getLastIdCard] = useHttpFetch(
    `${__PERSON_NEW_API_URL__}/persona/carte-identita/matricola/`,
    {
      options: {
        errorCatching: false,
        method: 'GET',
        loading: true,
        data: {},
      },
    }
  );

  const [cards, getCards] = useHttpFetch<HttpResponse<ResponseLot>, ResponseLot>(
    `${__PERSON_NEW_API_URL__}/persona/lotto-carte/last/`,
    {
      onBootstrap: false,
      options: {
        errorCatching: false,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponse<ResponseLot>>) => {
        return response.data.payload;
      },
    }
  );

  const [anomalie, getAnomalie] = useHttpFetch<
    HttpResponse<{ list: { className: string }[] }>,
    boolean
  >(`${__PERSON_NEW_API_URL__}/persona/note-anomalie/`, {
    onBootstrap: false,
    options: {
      errorCatching: false,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponse<{ list: { className: string }[] }>>) => {
      return response.data?.payload?.list && response.data.payload?.list?.length > 0;
    },
  });

  const [, printTag] = useHttpFetch(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/cartellino`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const [, internalPrint] = useHttpFetch(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/lato-interno`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const [, externalPrint] = useHttpFetch(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/lato-esterno`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: null,
      },
    }
  );

  const envelopePrint = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/busta`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: null,
      },
    }
  );

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, ReleaseIdCard>(
    `${__PERSON_NEW_API_URL__}/persona/ricerca-rilascio-cie`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: (response) => releaseIdCardResponseTransformer(response, userProfile),
    }
  );

  useEffect(() => {
    if (anpr.data) {
      const idSchedaSoggetto = (anpr.data as ReleaseIdCard).dati3002?.codicePersonaSipo;
      if (idSchedaSoggetto) {
        getAnomalie({ url: `${userProfile.datiLogin?.key?.codiceEnte}/${idSchedaSoggetto}` });
        (
          getLastIdCard({
            url: idSchedaSoggetto,
          }) as ResponsePromise<HttpResponse<LastIdCard>>
        ).then((response) => {
          if (['1', '*'].findIndex((value) => response?.data?.payload?.flag === value) >= 0) {
            setOpenIdCardErrorModal(true);
          }
        });
      }
      (getCards() as ResponsePromise<HttpResponse<ResponseLot>, ResponseLot>).then((response) => {
        if (!response) {
          setOpenCardsErrorModal(true);
        }
      });
    }
  }, [anpr.data]);

  useEffect(() => {
    //calling the anpr to find out whether ther permit card is expired or not
    const permessoDiSoggiorno = (anpr.data as ReleaseIdCard)?.dati3002?.permessoSoggiorno;

    const tDate = DateTime.now();

    const cittadinanza =
      ((anpr.data as ReleaseIdCard)?.dati3002?.cittadinanza as Array<Citizenship>) || [];

    const isExtraUE = !!cittadinanza?.find((item) => item?.flagUnioneEuropea === '0');

    if (isExtraUE) {
      //check whether permesso di soggiorno exists or not
      if (permessoDiSoggiorno) {
        //show error if permesso di soggiorno is expired
        if (permessoDiSoggiorno?.dataScadenza) {
          const permessoDiSoggiornoDataDiScadenza = DateTime.fromISO(
            permessoDiSoggiorno?.dataScadenza
          );

          if (
            tDate.startOf('day') > permessoDiSoggiornoDataDiScadenza.startOf('day') ||
            tDate.startOf('month') > permessoDiSoggiornoDataDiScadenza.startOf('month') ||
            tDate.startOf('year') > permessoDiSoggiornoDataDiScadenza.startOf('year')
          ) {
            setPermitError({
              open: true,
              value: 'Permesso di soggiorno scaduto o mancante',
            });
          }
        }
      } else {
        setPermitError({
          open: true,
          value: 'Permesso di soggiorno scaduto o mancante',
        });
      }
    }
  }, [anpr.data]);

  const onReleaseIdCard = useEventCallback((formikValues: ReleaseIdCard) => {
    const dataCard = transformIdCardRequest(
      formikValues,
      (cards.data as ResponseLot)?.numeroInizioLotto
    );

    (
      checkReleaseIdCard({
        data: dataCard,
      }) as ResponsePromise<void>
    ).then(() => {
      setOpenPrintModal(true);
    });
  });

  const onPrintTagQuestura = useEventCallback((formikValues: ReleaseIdCard, isComune: boolean) => {
    const data = transformPrintTagRequestQuestura(
      {
        ...(anpr.data as ReleaseIdCard),
        otherData: {
          ...((anpr.data as ReleaseIdCard).otherData || {}),
          ...(formikValues.otherData || {}),
        },
        document: {
          ...(anpr.data as ReleaseIdCard).document,
          ...formikValues.document,
        },
      },
      userProfile.datiLogin?.key?.userName || ''
    );
    (
      printTag({
        data: {
          ...data,
          stampaComune: isComune || false,
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      printPdf(response, 'Stampa Cartellino');
    });
  });

  const onPrintTagComune = useEventCallback((formikValues: ReleaseIdCard, isComune: boolean) => {
    const data = transformPrintTagRequestComune(
      {
        ...(anpr.data as ReleaseIdCard),
        otherData: {
          ...((anpr.data as ReleaseIdCard).otherData || {}),
          ...(formikValues.otherData || {}),
        },
        document: {
          ...(anpr.data as ReleaseIdCard).document,
          ...formikValues.document,
        },
      },
      userProfile.datiLogin?.key?.userName || ''
    );
    (
      printTag({
        data: {
          ...data,
          stampaComune: isComune || false,
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      printPdf(response, 'Stampa Cartellino');
    });
  });

  const onInternalPrint = useEventCallback((formikValues: ReleaseIdCard) => {
    const dataCard = transformIdCardRequest(
      formikValues,
      (cards.data as ResponseLot)?.numeroInizioLotto
    );

    (
      releaseIdCard({
        data: dataCard,
      }) as ResponsePromise<HttpResponse<ReleasePaperIdCard>>
    ).then((response) => {
      const {
        importoDirittiFissi = 0,
        importoDirittiSegreteria = 0,
        warning,
      } = response?.data.payload || {};

      if (warning) {
        setWarningModal({ open: true, value: warning });
      } else {
        const amountFixed = importoDirittiFissi
          ? Number.parseFloat(importoDirittiFissi?.toString())
          : 0.0;

        const amountSecretariat = importoDirittiSegreteria
          ? Number.parseFloat(importoDirittiSegreteria?.toString())
          : 0.0;

        const amountTotal = amountFixed + amountSecretariat;

        const data = transformInternalPrintRequest(
          {
            ...(anpr.data as ReleaseIdCard),
            otherData: {
              ...((anpr.data as ReleaseIdCard).otherData || {}),
              ...(formikValues.otherData || {}),
            },
            document: {
              ...(anpr.data as ReleaseIdCard).document,
              ...formikValues.document,
            },
          },
          userProfile.datiLogin?.key?.userName || '',
          amountTotal
        );
        (
          internalPrint({
            data,
            responseType: 'blob',
          }) as ResponsePromise<Blob>
        ).then((responsePdf) => {
          setInternalPrintVisibility(true);
          printPdf(responsePdf, 'Stampa Interno');
          toast.success('Operazione eseguita con successo');
        });
      }
    });
  });

  const onExternalPrint = useEventCallback((formikValues: ReleaseIdCard) => {
    const data = transformExternalPrintRequest({
      ...(anpr.data as ReleaseIdCard),
      otherData: {
        ...((anpr.data as ReleaseIdCard).otherData || {}),
        ...(formikValues.otherData || {}),
      },
      document: {
        ...(anpr.data as ReleaseIdCard).document,
        ...formikValues.document,
      },
    });
    (
      externalPrint({
        data,
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      setExternalPrintVisibility(true);
      printPdf(response, 'Stampa Esterno');
      toast.success('Operazione eseguita con successo');
    });
  });

  const onPrintEnvelope = useEventCallback((formikValues: ReleaseIdCard) => {
    const data = transformEnvelopePrintRequest({
      ...(anpr.data as ReleaseIdCard),
      otherData: {
        ...((anpr.data as ReleaseIdCard).otherData || {}),
        ...(formikValues.otherData || {}),
      },
      document: {
        ...(anpr.data as ReleaseIdCard).document,
        ...formikValues.document,
      },
    });
    (
      envelopePrint({
        data,
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => printPdf(response, 'Stampa Busta'));
  });

  const onCancel = useEventCallback(() => {
    setOpenCancelModal(true);
  });

  const navigate = useNavigate();

  const onAbort = useEventCallback(() => {
    if (modalType === ModalType.Cartellino) {
      setOpenPrintModal(false);
      return;
    }
    navigate('/sportello/rilascio-carta');
  });

  const [pdfModal, setPdfModal] = useState({
    data: '',
    open: false,
    name: '',
  });

  const printPdf = useCallback(
    (response: AxiosResponse<Blob> | undefined, name: string) => {
      const pdfCertificate = response?.data;
      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name,
            open: true,
          });
          setOpenPrintModal(false);
        });
      }
    },
    [setPdfModal, setOpenPrintModal]
  );

  if (!anpr.data && !anomalie.data) {
    return <></>;
  }

  const values: ReleaseIdCard = {
    ...((anpr.data as ReleaseIdCard) || {}),
    document: {
      ...((anpr.data as ReleaseIdCard)?.document || {}),
      disableExpatriationBan: (anpr.data as ReleaseIdCard).anomalie?.some(
        (item) =>
          item.codiceAnomalia === AnomalyType.Type34 || item.codiceAnomalia === AnomalyType.Type71
      )
        ? true
        : !(anpr.data as ReleaseIdCard)?.isItalian
        ? true
        : !!anomalie.data,
      isExpatriationBan: (anpr.data as ReleaseIdCard).anomalie?.some(
        (item) =>
          item.codiceAnomalia === AnomalyType.Type34 || item.codiceAnomalia === AnomalyType.Type71
      )
        ? true
        : !(anpr.data as ReleaseIdCard)?.isItalian
        ? true
        : (anpr.data as ReleaseIdCard).document?.isExpatriationBan,
      expirationDate: (anpr.data as ReleaseIdCard).document?.expirationDate,
      fixedLotNumber:
        (cards?.data as { numeroInizioLotto: string })?.numeroInizioLotto?.substring(0, 6) || '',
    },
  };

  return (
    <Fragment>
      <Breadcrumbs items={ReleaseIdCardCrumbs} />
      <PmiTitle title="Rilascio Carta Identità" />
      <div className="pmi-mt-4">
        <div className="pmi-mt-2">
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            isInitialValid={false}
            initialValues={values || initialValues}
            onSubmit={onReleaseIdCard}
            validateOnChange
            validateOnBlur
            validateOnMount
          >
            {({ values: valuesFormik }) => (
              <Form>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <ReleaseIdCardButtons
                      onCancel={onCancel}
                      setModalType={setModalType}
                      pressedButtons={pressedButtons}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <div className="pmi-px-md-4">
                      <ReleaseIdCardForm />
                    </div>
                  </Grid>
                </Grid>
                <ModalPrintCid
                  externalPrintVisibility={externalPrintVisibility}
                  internalPrintVisibility={internalPrintVisibility}
                  modalType={modalType}
                  onAbort={onAbort}
                  onExternalPrint={onExternalPrint}
                  onInternalPrint={onInternalPrint}
                  onPrintEnvelope={onPrintEnvelope}
                  onPrintTagComune={onPrintTagComune}
                  onPrintTagQuestura={onPrintTagQuestura}
                  openModal={openPrintModal}
                  setPressedButton={(el) => {
                    setPressedButtons((old) => {
                      if (old.find((element) => element === el)) {
                        return old;
                      }
                      return [...old, el];
                    });
                  }}
                  values={valuesFormik}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <CancelModal open={openCancelModal} onClose={setOpenCancelModal} />
      <ErrorModal
        id="blocking-id-card-dialog"
        open={openIdCardErrorModal}
        onClose={onAbort}
        handleError={onAbort}
        title="Errore bloccante"
        message="Attenzione. Persona con istruttoria in corso per rilascio CIE"
      />
      <ErrorModal
        id="blocking-cards-dialog"
        open={openCardsErrorModal}
        onClose={onAbort}
        handleError={onAbort}
        title="Errore bloccante"
        message="Attenzione. Lotto carte esaurito."
      />
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title={pdfModal.name}
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          setOpenPrintModal(true);
        }}
      />
      {warningModal.open ? (
        <ModalWarning
          open={warningModal.open}
          onClose={() => {
            setWarningModal({ open: false, value: '' });
            // apertura modale stampa
            setOpenPrintModal(true);
          }}
          message={warningModal.value}
        />
      ) : undefined}
      {permitError?.open ? (
        <ModalWarning
          open={permitError.open}
          onClose={() => {
            setPermitError({ open: false, value: '' });
          }}
          message={permitError.value}
        />
      ) : undefined}
    </Fragment>
  );
};

ReleaseIdCardPage.displayName = 'Release id card page component';

export default ReleaseIdCardPage;
